import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { PlataEnJuego as Plata } from '@/lib/supabase/tipos-bd'
import { pesos } from '@/shared/utilidades/formato'

import estilos from './PlataEnJuego.module.css'

/**
 * LA PLATA EN JUEGO · el panel oscuro del tablero.
 *
 * Es el dato por el que gerencia abre la pantalla y el único que no estaba: el
 * tablero contaba órdenes, horas y demoras, y ni una vez cuánta plata hay
 * encima de la mesa.
 *
 * VA EN OSCURO Y ES EL ÚNICO QUE VA EN OSCURO. Una pila de tarjetas blancas
 * iguales no tiene foco: el ojo entra por arriba a la izquierda y sale por
 * abajo sin detenerse. Un panel oscuro dentro de una página clara para el ojo
 * en seco, y por eso se gasta UNA vez y en lo más importante. Si mañana hay
 * dos paneles oscuros, no hay ninguno.
 *
 * Y es donde el oro por fin trabaja. Medido antes de esto: `--oro` aparecía
 * doce veces en seis archivos y ocho de ellas en el menú y en la pantalla de
 * ingreso; en las tres pantallas donde se pasa el día, cero. Sobre morado
 * profundo el oro de la marca da 9,2:1 —es la combinación del logo— y aquí
 * marca lo que decide el margen: la mano de obra.
 */

function usePlataEnJuego() {
  const consulta = useQuery({
    queryKey: ['plata-en-juego'],
    // Por si la conexión en vivo se cae sin avisar: cada minuto, solo.
    refetchInterval: 60 * 1000,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Plata | null> => {
      const { data, error } = await supabase.rpc('fn_plata_en_juego')
      if (error) throw new Error(mensajeDeError(error))
      const fila = (data as unknown as Plata[])?.[0]
      return fila ?? null
    },
  })

  return { plata: consulta.data ?? null, cargando: consulta.isLoading }
}

export function PlataEnJuego() {
  const { plata, cargando } = usePlataEnJuego()

  if (cargando || !plata) return null

  const conCliente = Number(plata.con_cliente_total ?? 0)
  const comprometida = Number(plata.comprometida_total ?? 0)
  const total = conCliente + comprometida

  /**
   * CERO TAMBIÉN ES UN DATO, y muchas veces el más importante.
   *
   * Este panel se escondía cuando no había plata viva, y con las veintitrés
   * órdenes recién importadas y ninguna cotizada la pantalla se quedó sin su
   * único foco justo el día en que más falta hacía. Un panel que desaparece en
   * cero no es discreto: deja la pantalla plana y hace creer que no pasa nada.
   *
   * Sin plata, el panel dice lo que SÍ hay: cuántas están esperando precio, que
   * es exactamente lo que hay que ir a hacer.
   */
  if (total <= 0) {
    return (
      <section className={`${estilos.panel} ${estilos.enEspera}`}>
        <div className={estilos.cuerpo}>
          <div className={estilos.cifra}>
            <p className={estilos.rotulo}>Todavía no hay plata en juego</p>
            <p className={estilos.total}>
              {plata.sin_cotizar_n}
              <span className={estilos.totalUnidad}>
                {plata.sin_cotizar_n === 1 ? ' orden' : ' órdenes'}
              </span>
            </p>
            <p className={estilos.pie}>
              Esperando precio · ninguna cotizada todavía
              {Number(plata.sin_cotizar_dias_max) >= 1 && (
                <>
                  {' · la más vieja lleva '}
                  <b className={estilos.tarde}>
                    {String(plata.sin_cotizar_dias_max).replace('.', ',')} días
                  </b>
                </>
              )}
            </p>
          </div>

          <div className={estilos.nota}>
            En cuanto se cotice la primera, aquí sale cuánto hay encima de la
            mesa y qué parte es mano de obra, que es lo que decide el margen.
          </div>
        </div>
      </section>
    )
  }

  /*
   * LAS SUMAS CUADRAN (0237). Johan: «no me cuadran las sumas». El total iba
   * con IVA y la partición sin IVA, y la partición dejaba fuera lo cotizado
   * en la aplicación. Ahora: total = base + IVA, y la base = mano de obra +
   * materiales + lo que World Office no separa. Cada cifra de la leyenda es
   * una parte de la base, y la barra es la base entera.
   */
  const mano = Number(plata.mano_de_obra ?? 0)
  const materiales = Number(plata.materiales ?? 0)
  const sinSeparar = Number(plata.sin_separar ?? 0)
  const base = Number(plata.base_sin_iva ?? mano + materiales + sinSeparar)
  const iva = Number(plata.iva ?? 0)
  const parte = (v: number) => (base > 0 ? (v / base) * 100 : 0)
  // El porcentaje, sobre lo que sí separa la mano de obra.
  const pctMano = Number(plata.pct_mano_de_obra ?? 0)

  return (
    <section className={estilos.panel}>

      <div className={estilos.cuerpo}>
        <div className={estilos.cifra}>
          <p className={estilos.rotulo}>La plata en juego</p>
          <p className={estilos.total}>{pesos(total)}</p>
          <p className={estilos.pie}>
            {plata.con_cliente_n + plata.comprometida_n} cotizaciones vivas · base{' '}
            {pesos(base)} + IVA {pesos(iva)}
          </p>
        </div>

        <div className={estilos.particion}>
          {/* La partición que decide el margen en una empresa que instala: el
              material se revende casi sin margen, lo que deja es la hora. */}
          <div className={estilos.barra}>
            <span className={estilos.mano} style={{ width: `${parte(mano)}%` }} />
            <span className={estilos.materiales} style={{ width: `${parte(materiales)}%` }} />
            {sinSeparar > 0 && (
              <span className={estilos.sinSeparar} style={{ width: `${parte(sinSeparar)}%` }} />
            )}
          </div>
          <div className={estilos.leyenda}>
            <span>
              <i className={estilos.pMano} />
              Mano de obra <b>{pesos(mano)}</b>
            </span>
            <span>
              <i className={estilos.pMateriales} />
              Materiales <b>{pesos(materiales)}</b>
            </span>
            {sinSeparar > 0 && (
              <span title="Cotizaciones de World Office que no separan la mano de obra">
                <i className={estilos.pSinSeparar} />
                Sin separar <b>{pesos(sinSeparar)}</b>
              </span>
            )}
          </div>
        </div>

        <div className={estilos.margen}>
          <p className={estilos.margenCifra}>{pctMano.toFixed(1).replace('.', ',')}%</p>
          <p className={estilos.margenRotulo}>es mano de obra</p>
        </div>
      </div>

      <div className={estilos.reparto}>
        <div className={estilos.mitad}>
          <span className={estilos.mitadRotulo}>Donde el cliente</span>
          <span className={estilos.mitadCifra}>{pesos(conCliente)}</span>
          <span className={estilos.mitadPie}>
            {plata.con_cliente_n}{' '}
            {plata.con_cliente_n === 1 ? 'esperando respuesta' : 'esperando respuesta'}
            {Number(plata.con_cliente_dias_max) > 0 && (
              <>
                {' · '}
                <b className={Number(plata.con_cliente_dias_max) > 7 ? estilos.tarde : undefined}>
                  la más vieja, {String(plata.con_cliente_dias_max).replace('.', ',')} días
                </b>
              </>
            )}
          </span>
        </div>

        <div className={estilos.mitad}>
          <span className={estilos.mitadRotulo}>Vendido y sin ejecutar</span>
          <span className={estilos.mitadCifra}>{pesos(comprometida)}</span>
          <span className={estilos.mitadPie}>
            {plata.comprometida_n} {plata.comprometida_n === 1 ? 'orden' : 'órdenes'} por hacer
          </span>
        </div>
      </div>
    </section>
  )
}
